var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import FocusLock from 'react-focus-lock';
import RemoveScroll from '../RemoveScroll/RemoveScroll';
import BreakpointProvider, { useBreakpoint } from '../../util/Breakpoint.context';
import * as Styled from './Drawer.styled';
var TRANSITION_DURATION = 300;
var ESC_KEYS = ['Esc', 'Escape'];
/**
 * A `Drawer` is a controlled component which provides a surface on top of the main content
 * which slides into the viewport from the left or right side of the screen.
 */
var Drawer = function (_a) {
    var children = _a.children, _b = _a.isOpen, isOpen = _b === void 0 ? false : _b, onRequestClose = _a.onRequestClose, _c = _a.hasBackdrop, hasBackdrop = _c === void 0 ? true : _c, _d = _a.position, position = _d === void 0 ? 'right' : _d, _e = _a.layoutWithSidePadding, layoutWithSidePadding = _e === void 0 ? true : _e, _f = _a.layoutTopMargin, layoutTopMargin = _f === void 0 ? 12 : _f, props = __rest(_a, ["children", "isOpen", "onRequestClose", "hasBackdrop", "position", "layoutWithSidePadding", "layoutTopMargin"]);
    var medium = useBreakpoint().medium;
    var drawerContents = (React.createElement(React.Fragment, null,
        hasBackdrop && (React.createElement(Styled.Backdrop, { "data-testid": "drawer_backdrop", onMouseDown: onRequestClose, transitionDuration: TRANSITION_DURATION, position: position })),
        React.createElement(FocusLock, { returnFocus: true, disabled: !hasBackdrop },
            React.createElement(Styled.DrawerContent, __assign({ transitionDuration: TRANSITION_DURATION, position: position, layoutTopMargin: layoutTopMargin, layoutWithSidePadding: layoutWithSidePadding }, props), children))));
    useEffect(function () {
        var handleKeyDown = function (e) {
            if (isOpen && ESC_KEYS.includes(e.key)) {
                onRequestClose(e);
            }
        };
        document.addEventListener('keydown', handleKeyDown, { capture: true });
        return function () {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onRequestClose]);
    return (React.createElement(CSSTransition, { appear: true, in: isOpen, classNames: "animate-".concat(position), timeout: TRANSITION_DURATION, unmountOnExit: true },
        React.createElement(Styled.Drawer, { role: "dialog", "data-position": position, hasBackdrop: hasBackdrop }, hasBackdrop || !medium ? (React.createElement(RemoveScroll, null, drawerContents)) : (drawerContents))));
};
var DrawerContainer = function (props) {
    var containerRef = useRef(document.createElement('div'));
    // Setup / teardown
    useEffect(function () {
        var element = containerRef.current;
        document.body.appendChild(element);
        return function () {
            document.body.removeChild(element);
        };
    }, []);
    return createPortal(React.createElement(BreakpointProvider, null,
        React.createElement(Drawer, __assign({}, props))), containerRef.current);
};
export { Drawer as DrawerBody };
export default DrawerContainer;
